"use client";

import { useEffect } from "react";
import {
  Box as MantineBox,
  Button as MantineButton,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";
import Link from "@/components/Link";
import { logError } from "@/lib/client/errors";

/**
 * Shown when loading a blog post fails for a transient reason (backend blip,
 * network error). Genuine missing posts still render the 404 page — this
 * boundary only catches thrown fetch errors, and offers a retry instead of
 * dead-ending the reader.
 */
export default function BlogPostError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    logError(error, { source: "blog-post-error-boundary" });
  }, [error]);

  return (
    <MantineBox
      style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "96px 16px",
        textAlign: "center",
      }}
    >
      <MantineTitle order={2} mb="sm">
        THIS POST IS TAKING LONGER THAN USUAL
      </MantineTitle>
      <MantineText c="dimmed" mb="xl">
        We couldn&apos;t load the article just now. It&apos;s usually temporary.
      </MantineText>
      <MantineBox style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        <MantineButton onClick={() => reset()}>TRY AGAIN</MantineButton>
        <MantineButton component={Link} href="/blog" variant="outline">
          BACK TO BLOG
        </MantineButton>
      </MantineBox>
    </MantineBox>
  );
}
